<template>
  <div v-if="preview" class="lp-daterangepicker lp-daterangepicker-preview">
    <span v-if="labelTop" class="lp-daterangepicker-label font-weight-medium">
      <slot name="label-top" :props="props">
        {{ labelTop }}
        <span v-if="!preview && required" class="text-blue">*</span>
      </slot>
    </span>
    <lp-number-input
      v-model="startValue"
      label-top="Start value"
      hide-controls
      :preview="preview"
    />
    {{ previewSeparator }}
    <lp-number-input
      v-model="endValue"
      label-top="End value"
      hide-controls
      :preview="preview"
    />
  </div>
  <div v-else class="lp-daterangepicker">
    <lp-select
      v-model="selected"
      :options="options"
      :label-by="labelBy"
      :track-by="trackBy"
      :status="status"
      :message="message"
      placement="bottom"
      :placeholder="placeholder"
      class="w-100"
      :label-top="labelValue"
      :required="required"
      :disabled="disabled"
      v-bind="$attrs"
      @update:model-value="updateSelection"
    />

    <lp-modal v-model="modal" size="sm" compact @modal-close="onCancel">
      <template #header>
        <h3 style="margin: 0">{{ customRangeLabel }}</h3>
      </template>
      <template #body style="max-height: 20vh !important">
        <lp-number-input
          v-model="startValue"
          label-top="Start value"
          hide-controls
        />
        <lp-number-input
          v-model="endValue"
          label-top="End value"
          hide-controls
        />
        <div v-if="validationMessage" class="mt-1 text-red">
          {{ validationMessage }}
        </div>
      </template>
      <template #actions>
        <lp-button @click="onCancel">Cancel</lp-button>
        <lp-button type="primary" @click="updateRange">Confirm</lp-button>
      </template>
    </lp-modal>
  </div>
</template>

<script setup lang="ts">
import { watch, computed } from 'vue'
import { formatRange, NumberFormat } from '@/helpers/numbers'
import { useRange } from '@/helpers/useRange'

defineOptions({
  name: 'lp-numberrangepicker',
  inheritAttrs: false,
})

interface Option {
  name: string
  value: string
}

const emit = defineEmits(['update-range', 'update-selection'])

interface Props {
  modelValue: number | null | string
  format?: NumberFormat
  start?: number | string | null
  end?: number | string | null
  status?: string
  labelTop?: string | null
  placeholder?: string | null
  message?: string | null
  labelBy?: string | null
  trackBy?: string | null
  customRangeLabel?: string | null
  disabled?: boolean
  required?: boolean | null
  preview?: boolean | null
  previewSeparator?: string | null
  options: Option[]
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  options: () => [],
  format: NumberFormat.Decimal,
  start: null,
  end: null,
  labelTop: null,
  placeholder: null,
  labelBy: 'name',
  trackBy: 'value',
  status: 'neutral',
  message: null,
  customRangeLabel: 'Custom Range',
  disabled: false,
  required: false,
  preview: false,
  previewSeparator: '',
})

const customRangeOption = computed(() => {
  let label = props.customRangeLabel
  if (startValue.value === endValue.value) {
    label += ` (${formatRange(
      [startValue.value, endValue.value],
      props.format as NumberFormat,
    )})`
  } else if (startValue.value || endValue.value) {
    label += ` (${formatRange(
      [startValue.value, endValue.value],
      props.format as NumberFormat,
    )})`
  }

  return { name: label, value: CUSTOM_RANGE }
})

const options = computed(() => {
  return [...props.options, customRangeOption.value]
})

const {
  CUSTOM_RANGE,
  selected,
  modal,
  updateSelection,
  updateRange,
  startValue,
  endValue,
  labelValue,
  validRange,
  validationMessage,
  onCancel,
} = useRange(props, emit)

watch(
  () => props.modelValue,
  (newValue) => {
    selected.value = newValue
  },
)

watch(
  () => props.start,
  (newStartValue) => {
    startValue.value = newStartValue

    if (newStartValue !== null) {
      selected.value = CUSTOM_RANGE
    } else {
      selected.value = props.modelValue || null
    }
  },
  { immediate: true },
)

watch(
  () => props.end,
  (newEndValue) => {
    endValue.value = newEndValue

    if (newEndValue !== null) {
      selected.value = CUSTOM_RANGE
    } else {
      selected.value = props.modelValue || null
    }
  },
  { immediate: true },
)
</script>
